import { useState, useEffect } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import { StepProgressBar } from "@/components/StepProgressBar";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function getGoogleOAuthUrl(params?: Record<string, string>): string {
  const rdtCid = localStorage.getItem("rdt_cid");
  const url = new URL(`${BASE}/api/auth/google`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  if (rdtCid) url.searchParams.set("rdt_cid", rdtCid);
  return url.pathname + url.search;
}

const STEPS = ["Create account", "Verify email", "Start Pro"];

function getInterval(): "monthly" | "annual" {
  const p = new URLSearchParams(window.location.search);
  const v = p.get("interval");
  return v === "annual" ? "annual" : "monthly";
}

// A member offer link carries its promo code here. It stays in the URL so it
// survives the verification email and Google sign-in.
function getPromo(): string | undefined {
  const v = new URLSearchParams(window.location.search).get("promo");
  return v && /^[A-Za-z0-9_-]{1,64}$/.test(v) ? v : undefined;
}

function getReturnTo(interval: "monthly" | "annual"): string {
  const promo = getPromo();
  return `/signup/pro?interval=${interval}${promo ? `&promo=${promo}` : ""}`;
}

const GOOGLE_SVG = (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
    <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

function BillingToggle({
  interval,
  onChange,
  monthlyPrice,
  annualPerMonth,
  savingsPct,
  pricesLoading,
}: {
  interval: "monthly" | "annual";
  onChange: (v: "monthly" | "annual") => void;
  monthlyPrice: number | null;
  annualPerMonth: number | null;
  savingsPct: number | null;
  pricesLoading: boolean;
}) {
  return (
    <div className="flex flex-col items-center mb-4 gap-1.5">
      <div className="inline-flex items-center bg-gray-100 rounded-full p-1 gap-1">
        <button
          type="button"
          onClick={() => onChange("monthly")}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            interval === "monthly"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Monthly
          {!pricesLoading && monthlyPrice !== null && (
            <span className={`text-xs ${interval === "monthly" ? "text-blue-600 font-semibold" : "text-gray-400"}`}>
              ${monthlyPrice}/mo
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onChange("annual")}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            interval === "annual"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Annual
          {!pricesLoading && annualPerMonth !== null && (
            <span className={`text-xs ${interval === "annual" ? "text-blue-600 font-semibold" : "text-gray-400"}`}>
              ${annualPerMonth}/mo
            </span>
          )}
          {!pricesLoading && savingsPct !== null && (
            interval === "annual" ? (
              <span className="ml-0.5 text-xs font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                Save {savingsPct}%
              </span>
            ) : (
              <span className="ml-0.5 text-xs font-medium bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">
                Save {savingsPct}%
              </span>
            )
          )}
        </button>
      </div>
      {pricesLoading && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading prices…
        </div>
      )}
    </div>
  );
}

function CheckInboxStep({
  email,
  interval,
  onBack,
  onShowLogin,
}: {
  email: string;
  interval: "monthly" | "annual";
  onBack: () => void;
  onShowLogin: () => void;
}) {
  const { resendVerification } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");

  const handleResend = async () => {
    setResending(true);
    setResendError("");
    try {
      await resendVerification(email);
      setResent(true);
    } catch {
      setResendError("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="text-center space-y-4">
      <div className="mx-auto mb-2 flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
        <Mail className="h-8 w-8 text-blue-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900">Check your inbox</h2>
      <p className="text-sm text-gray-500">
        We sent a verification link to <strong>{email}</strong>. Click the link in the email to activate your account.
      </p>
      <p className="text-xs text-gray-400">Didn't receive the email? Check your spam folder or resend it.</p>
      {resent ? (
        <div className="flex items-center justify-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 shrink-0 text-green-600" />
          <span>Verification email resent!</span>
        </div>
      ) : (
        <Button variant="outline" className="w-full" onClick={handleResend} disabled={resending}>
          {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Resend verification email
        </Button>
      )}
      {resendError && <p className="text-sm text-red-600">{resendError}</p>}
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <p className="text-sm text-gray-600">Already verified your email?</p>
        <Button variant="default" className="w-full" onClick={onShowLogin}>
          Sign in to continue →
        </Button>
        <a
          href={getGoogleOAuthUrl({ returnTo: getReturnTo(interval) })}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {GOOGLE_SVG}
          Continue with Google
        </a>
      </div>
      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          ← Start over
        </button>
      </div>
    </div>
  );
}

function LoginStep({
  interval,
  onSuccess,
  onBack,
}: {
  interval: "monthly" | "annual";
  onSuccess: () => void;
  onBack: () => void;
}) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      onSuccess();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Sign in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm text-gray-600">Sign in to complete your Pro upgrade.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="login-password">Password</Label>
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            required
            autoComplete="current-password"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign In &amp; Continue
        </Button>
      </form>
      <div className="relative my-1">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs text-gray-400">
          <span className="bg-white px-2">or</span>
        </div>
      </div>
      <a
        href={getGoogleOAuthUrl({ returnTo: getReturnTo(interval) })}
        className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        {GOOGLE_SVG}
        Sign in with Google
      </a>
      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          ← Back to inbox screen
        </button>
      </div>
    </div>
  );
}

function CheckoutStep({ interval }: { interval: "monthly" | "annual" }) {
  const [error, setError] = useState("");
  const [triggering, setTriggering] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const doCheckout = async () => {
      try {
        const res = await fetch(`${BASE}/api/billing/checkout`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ interval, rdt_cid: localStorage.getItem("rdt_cid") || undefined, promo: getPromo() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
        if (data.url && !cancelled) {
          window.location.href = data.url;
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
          setTriggering(false);
        }
      }
    };
    doCheckout();
    return () => { cancelled = true; };
  }, [interval]);

  return (
    <div className="text-center space-y-4">
      {triggering ? (
        <>
          <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Redirecting to checkout…</h2>
          <p className="text-sm text-gray-500">Setting up your Pro plan. Please wait.</p>
        </>
      ) : (
        <>
          <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
            <span className="text-2xl font-bold text-red-600">!</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Checkout unavailable</h2>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <p className="text-sm text-gray-500">
            You can still upgrade from your{" "}
            <Link href="/upgrade" className="text-blue-600 hover:underline font-medium">
              dashboard
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
}

export default function ProSignupPage() {
  usePageTitle("Pro Signup");
  const { user, loading, register } = useAuth();
  const [interval, setInterval] = useState<"monthly" | "annual">(getInterval);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkInboxEmail, setCheckInboxEmail] = useState<string | null>(null);
  const [showLoginForm, setShowLoginForm] = useState(false);

  const [liveMonthly, setLiveMonthly] = useState<number | null>(null);
  const [liveAnnualPerMonth, setLiveAnnualPerMonth] = useState<number | null>(null);
  const [pricesLoading, setPricesLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/api/billing/prices`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => {
        if (data?.monthly?.amount != null) setLiveMonthly(data.monthly.amount);
        if (data?.annual?.perMonth != null) setLiveAnnualPerMonth(data.annual.perMonth);
        else if (data?.annual?.amount != null) setLiveAnnualPerMonth(Math.round((data.annual.amount / 12) * 100) / 100);
      })
      .catch(() => {})
      .finally(() => setPricesLoading(false));
  }, []);

  const savingsPct =
    liveMonthly !== null && liveAnnualPerMonth !== null && liveMonthly > 0
      ? Math.round(((liveMonthly - liveAnnualPerMonth) / liveMonthly) * 100)
      : null;

  useEffect(() => {
    if (!loading && user) {
      setStep(3);
    }
  }, [loading, user]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("interval", interval);
    window.history.replaceState(null, "", url.toString());
  }, [interval]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!agreedToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await register(name, email, password, getReturnTo(interval), agreedToTerms);
      if (result.emailVerificationRequired && result.email) {
        setCheckInboxEmail(result.email);
        setStep(2);
      } else {
        setStep(3);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const cardTitle = () => {
    if (step === 1) return "Create your account";
    if (step === 2) return showLoginForm ? "Sign in to continue" : "Verify your email";
    return "Starting your Pro plan";
  };

  const cardDescription = () => {
    if (step === 1) return "Get started with QuoTrak Pro";
    if (step === 2) return showLoginForm ? "Sign in to complete your Pro upgrade" : "One more step before checkout";
    return "Almost there — heading to secure checkout";
  };

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="absolute top-4 left-4">
        <a
          href="https://quotrak.com"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </a>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/logo-new.png"
            alt="QuoTrak"
            style={{ height: "3.5rem", width: "auto", objectFit: "contain", display: "inline-block" }}
          />
        </div>

        <div className="mb-2">
          <StepProgressBar steps={STEPS} currentStep={step} />
        </div>

        <Card>
          <CardHeader className="text-center pb-2">
            <div className="inline-block mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                Pro Plan · {interval === "annual" ? "Annual" : "Monthly"}
              </span>
            </div>
            <CardTitle className="text-2xl mt-1">{cardTitle()}</CardTitle>
            <CardDescription>{cardDescription()}</CardDescription>
            {getPromo() && (
              <p className="text-xs text-gray-500 mt-1">
                Your code <strong>{getPromo()}</strong> carries through to checkout, where you'll see the discounted price.
              </p>
            )}
          </CardHeader>

          <CardContent className="pt-4">
            {step === 1 && (
              <>
                <BillingToggle
                  interval={interval}
                  onChange={setInterval}
                  monthlyPrice={liveMonthly}
                  annualPerMonth={liveAnnualPerMonth}
                  savingsPct={savingsPct}
                  pricesLoading={pricesLoading}
                />

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="flex items-start gap-2.5 pt-1">
                    <input
                      id="agree-terms-pro"
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="agree-terms-pro" className="text-sm text-gray-600 leading-snug cursor-pointer select-none">
                      I have read and agree to the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <Button type="submit" className="w-full" disabled={submitting || !agreedToTerms}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Account
                  </Button>
                </form>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs text-gray-400">
                    <span className="bg-white px-2">or</span>
                  </div>
                </div>

                <a
                  href={getGoogleOAuthUrl({ returnTo: getReturnTo(interval) })}
                  onClick={(e) => {
                    if (!agreedToTerms) {
                      e.preventDefault();
                      setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
                    }
                  }}
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {GOOGLE_SVG}
                  Sign up with Google
                </a>

                <div className="mt-4 text-center text-sm text-gray-600">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setShowLoginForm(true)}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </div>

                {showLoginForm && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <LoginStep
                      interval={interval}
                      onSuccess={() => setStep(3)}
                      onBack={() => setShowLoginForm(false)}
                    />
                  </div>
                )}
              </>
            )}

            {step === 2 && !showLoginForm && checkInboxEmail && (
              <CheckInboxStep
                email={checkInboxEmail}
                interval={interval}
                onBack={() => {
                  setStep(1);
                  setCheckInboxEmail(null);
                }}
                onShowLogin={() => setShowLoginForm(true)}
              />
            )}

            {step === 2 && showLoginForm && (
              <LoginStep
                interval={interval}
                onSuccess={() => setStep(3)}
                onBack={() => setShowLoginForm(false)}
              />
            )}

            {step === 3 && <CheckoutStep interval={interval} />}
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
}
